import { useApplications } from '@services/offers/queries/useApplications'
import { useFlats } from '@services/flats/queries/useFlats'
import { SNA_IMAGE_PREFIX } from '@services/utils'
import {
  Building2,
  CalendarClock,
  ExternalLink,
  FileText,
  Info,
  Loader2,
  type LucideIcon,
  MapPin,
  Save,
  ShieldCheck,
  Star,
  Tag,
  User as UserIcon,
  Users
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import type { FlatDetail, OfferCreate, OfferDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { MoneyInput } from '@/components/ui/money-input'
import { MultiCombobox } from '@/components/ui/multi-combobox'
import { Switch } from '@/components/ui/switch'

import {
  depositMaxAllowed,
  depositMonths,
  QUICK_FILL_MONTHS,
  round2
} from '../deposit'

const EXPENSE_FIELDS: { key: keyof OfferCreate; labelKey: string }[] = [
  {
    key: 'monthly_advance_prescription',
    labelKey: 'admin.form.monthly_advance_prescription'
  },
  { key: 'electricity', labelKey: 'admin.form.electricity' },
  { key: 'gas', labelKey: 'admin.form.gas' },
  { key: 'internet_tv', labelKey: 'admin.form.internet_tv' }
]

function incomeRatio(income: number, total: number): number | null {
  if (income && total) {
    return Math.round(((total * 100 + 300) / income) * 100) / 100
  }
  return null
}

function getRatio(income: number, total: number) {
  const r = incomeRatio(income, total)
  return r == null ? '' : ` · ${r} %`
}

function Stat({
  icon: Icon,
  label,
  value,
  fallback,
  tone,
  className
}: {
  icon: LucideIcon
  label: string
  value: string | null
  fallback: string
  tone?: 'warn'
  className?: string
}) {
  return (
    <div
      className={`rounded-xl bg-white px-3 py-2 ${className ?? ''}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3 text-primary" />
        {label}
      </div>
      <div
        className={`mt-0.5 truncate text-sm font-semibold ${
          value == null
            ? 'text-ink/30'
            : tone === 'warn'
              ? 'text-peach'
              : 'text-ink'
        }`}
      >
        {value ?? fallback}
      </div>
    </div>
  )
}

// `status` isn't part of OfferCreate but the create endpoint accepts it.
type FormValues = OfferCreate & { status?: string }

type OfferFormProps = {
  onFinish: (values: OfferCreate) => void
  initialValues?: Partial<OfferDetail>
  applications?: string[]
}

export function OfferForm({
  onFinish,
  initialValues,
  applications
}: OfferFormProps) {
  const { t } = useTranslation('offers')
  const [searchParams] = useSearchParams()
  // Zámerne bez filtra na stav. Ponuka sa dohaduje aj na byte, ktorý ešte nie
  // je dostupný — a dvojitému prenajatiu bráni až prechod ponuky do RESERVED,
  // ktorý dostupnosť vyžaduje (app/modules/offer/services.py).
  const { data: flatsData, isLoading } = useFlats({
    params: { limit: -1 }
  })
  const [appSearch, setAppSearch] = useState('')
  const { data: appsData, isLoading: appsLoading } = useApplications({
    search: appSearch,
    limit: -1
  })

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      flat: initialValues?.flat?.id || searchParams.get('flat') || '',
      applications: applications ?? [],
      status: initialValues?.status || 'CREATED',
      has_inspection: initialValues?.has_inspection ?? false,
      deposit_amount: initialValues?.deposit_amount ?? undefined,
      rent: initialValues?.rent ?? initialValues?.flat?.rent ?? 0,
      monthly_advance_prescription:
        initialValues?.monthly_advance_prescription ?? 0,
      electricity: initialValues?.electricity ?? 0,
      gas: initialValues?.gas ?? 0,
      internet_tv: initialValues?.internet_tv ?? 0,
      deadline: initialValues?.deadline
        ? initialValues.deadline.slice(0, 10)
        : undefined
    }
  })

  const flats = flatsData?.results ?? []

  // Autofill rent/utilities from a preselected flat (e.g. ?flat=… on first load).
  useEffect(() => {
    const flatId = searchParams.get('flat')
    const flat = flats.find((f) => f.id === flatId)
    if (flat) applyFlat(flat)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatsData])

  function applyFlat(flat: FlatDetail) {
    setValue('rent', flat.rent || 0)
    setValue('monthly_advance_prescription', flat.monthly_advance_prescription || 0)
    setValue('gas', flat.gas || 0)
    setValue('internet_tv', flat.internet_tv || 0)
    setValue('electricity', flat.electricity || 0)
  }

  const watched = watch()
  const totalExpenses =
    (Number(watched.rent) || 0) +
    (Number(watched.monthly_advance_prescription) || 0) +
    (Number(watched.electricity) || 0) +
    (Number(watched.gas) || 0) +
    (Number(watched.internet_tv) || 0)
  // The deposit is an absolute amount now, but it is still capped at three
  // times the monthly costs - and that ceiling moves with the rent.
  const depositMax = depositMaxAllowed(totalExpenses)
  const depositAmount = Number(watched.deposit_amount) || 0
  const depositOverCap = depositAmount > depositMax
  const months = depositMonths(depositAmount, totalExpenses)

  const flatOptions: ComboboxOption[] = flats.map((f) => ({
    value: String(f.id),
    label: f.title || String(f.id)
  }))
  const selectedFlat =
    flats.find((f) => f.id === watched.flat) || initialValues?.flat || null

  const appOptions: ComboboxOption[] = useMemo(
    () =>
      (appsData?.results ?? []).map((a) => ({
        value: String(a.id),
        label: `${a.user_detail?.email ?? a.id}${getRatio(
          a.user_detail?.income ?? 0,
          totalExpenses
        )}`
      })),
    [appsData, totalExpenses]
  )
  const selectedApps = (appsData?.results ?? []).filter((a) =>
    (watched.applications ?? []).includes(a.id ?? '')
  )

  const moneyField = (name: keyof OfferCreate, labelKey: string) => (
    <Controller
      key={name}
      control={control}
      name={name}
      render={({ field }) => (
        <div>
          <Label className="mb-1.5 block">{t(labelKey)}</Label>
          <MoneyInput
            className="h-10"
            value={field.value as number | null | undefined}
            onChange={(v) => field.onChange(v ?? undefined)}
            onBlur={field.onBlur}
          />
        </div>
      )}
    />
  )

  if (isLoading || !flatsData)
    return (
      <div className="flex items-center justify-center py-20 text-primary">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )

  return (
    <form
      onSubmit={handleSubmit((values) => onFinish(values))}
      className="space-y-6"
    >
      <Card className="space-y-6 p-6">
        {/* Finances */}
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Tag className="h-5 w-5" />
          </span>
          <h3 className="m-0 font-display text-lg font-bold tracking-tight text-ink">
            {t('admin.form.createTitle')}
          </h3>
        </div>

        {/* Flat + applications */}
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <Controller
            control={control}
            name="flat"
            render={({ field }) => (
              <div>
                <Label className="mb-1.5 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-primary" />
                  {t('admin.form.flat')}
                </Label>
                <Combobox
                  value={field.value || undefined}
                  onChange={(v) => {
                    field.onChange(v ?? '')
                    const flat = flats.find((f) => f.id === v)
                    if (flat) applyFlat(flat)
                  }}
                  options={flatOptions}
                  placeholder={t('admin.form.select_flat')}
                />
              </div>
            )}
          />

          <Controller
            control={control}
            name="applications"
            render={({ field }) => (
              <div>
                <Label className="mb-1.5 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-primary" />
                  {t('admin.form.applications')}
                </Label>
                <MultiCombobox
                  value={field.value ?? []}
                  onChange={field.onChange}
                  onSearch={setAppSearch}
                  options={appOptions}
                  loading={appsLoading}
                  placeholder={t('admin.form.select_application')}
                />
              </div>
            )}
          />
        </div>

        {/* Rent + deposit */}
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          {moneyField('rent', 'admin.form.rent_amount')}

          <Controller
            control={control}
            name="deposit_amount"
            rules={{ min: 0, max: depositMax }}
            render={({ field }) => (
              <div>
                <Label className="mb-1.5 flex items-center gap-1.5">
                  {t('admin.form.deposit')}
                  <span
                    title={t('admin.offerWidget.depositCap')}
                    className="inline-flex cursor-help text-ink/35 hover:text-primary"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </Label>
                <MoneyInput
                  className={`h-10 ${depositOverCap ? 'border-peach' : ''}`}
                  value={field.value as number | null | undefined}
                  onChange={(v) => field.onChange(v ?? undefined)}
                  onBlur={field.onBlur}
                />
                {/* The deposit no longer follows the rent on its own, so offer
                    the usual multiples as one click. */}
                <div className="mt-1.5 flex items-center gap-1.5">
                  {QUICK_FILL_MONTHS.map((m) => (
                    <Button
                      key={m}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      disabled={!totalExpenses}
                      onClick={() => field.onChange(round2(totalExpenses * m))}
                    >
                      ×{m}
                    </Button>
                  ))}
                  <span className="text-xs text-muted-foreground">
                    {t('admin.form.depositOfMonthly', {
                      months: months.toFixed(1)
                    })}
                  </span>
                </div>
                {depositOverCap && (
                  <p className="mt-1.5 text-xs font-medium text-peach">
                    {t('admin.form.depositOverCap', { max: depositMax })}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* Monthly payments */}
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 lg:grid-cols-4">
          {EXPENSE_FIELDS.map((f) => moneyField(f.key, f.labelKey))}
        </div>

        {/* Settings */}
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <Controller
            control={control}
            name="deadline"
            // The client is told how long the offer is held for, and the
            // reminder emails are timed from it.
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <div>
                <Label className="mb-1.5 flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-primary" />
                  {t('admin.form.deadline')}
                </Label>
                <DatePicker
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  min={new Date().toISOString().slice(0, 10)}
                  invalid={!!fieldState.error}
                />
                {fieldState.error && (
                  <p className="m-0 mt-1 text-xs font-semibold text-destructive">
                    {t('admin.form.deadline_required')}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="has_inspection"
            render={({ field }) => (
              <Card
                variant="light"
                className="flex items-center justify-between gap-3 px-4 py-3 sm:self-end"
              >
                <Label className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  {t('admin.form.has_inspection')}
                </Label>
                <Switch
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                />
              </Card>
            )}
          />
        </div>
      </Card>

      {selectedApps.length > 0 && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </span>
            <h3 className="m-0 font-display text-base font-bold tracking-tight text-ink">
              {t('admin.form.selected_applications')}
            </h3>
            <span className="ml-auto rounded-full bg-ink/[0.06] px-2.5 py-0.5 text-xs font-semibold text-ink/70">
              {selectedApps.length}
            </span>
          </div>

          <div
            className={`grid gap-3 ${
              selectedApps.length > 1 ? 'lg:grid-cols-2' : 'grid-cols-1'
            }`}
          >
            {selectedApps.map((a) => {
              const u = a.user_detail
              const fullName =
                `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() ||
                u?.email ||
                String(a.id)
              const initials =
                `${u?.first_name?.[0] ?? ''}${u?.last_name?.[0] ?? ''}`.toUpperCase() ||
                (u?.email?.[0] ?? '?').toUpperCase()
              const budget =
                a.rent_min != null || a.rent_max != null
                  ? `${a.rent_min ?? '?'} – ${a.rent_max ?? '?'} €`
                  : null
              const locations = a.locations?.map((l) => l.name).join(', ')

              return (
                <Card
                  key={a.id}
                  variant="light"
                  className="flex flex-col gap-3 p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {initials}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold text-ink">
                          {fullName}
                        </span>
                        {u?.rating != null && (
                          <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-full bg-peach/15 px-2 py-0.5 text-xs font-semibold text-ink">
                            <Star className="h-3 w-3 fill-peach text-peach" />
                            {u.rating}
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {u?.email}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {locations && (
                      <Stat
                        icon={MapPin}
                        label={t('admin.form.preferred_locations')}
                        value={locations}
                        fallback={t('admin.form.no_data')}
                        className="col-span-2"
                      />
                    )}
                    <Stat
                      icon={Tag}
                      label={t('admin.form.rent_budget')}
                      value={budget}
                      fallback={t('admin.form.no_data')}
                    />
                    <Stat
                      icon={CalendarClock}
                      label={t('admin.form.arrival_date')}
                      value={
                        a.arrival_date
                          ? new Date(a.arrival_date).toLocaleDateString('sk-SK')
                          : null
                      }
                      fallback={t('admin.form.no_data')}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 border-t border-ink/[0.06] pt-3">
                    {(u?.id || a.user) && (
                      <Link
                        to={`/admin/users/${u?.id ?? a.user}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full bg-ink/[0.04] px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-ink/[0.08]"
                      >
                        <UserIcon className="h-3.5 w-3.5 text-primary" />
                        {t('admin.form.view_user')}
                        <ExternalLink className="h-3 w-3 text-ink/40" />
                      </Link>
                    )}
                    {a.id && (
                      <Link
                        to={`/admin/applications/${a.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full bg-ink/[0.04] px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-ink/[0.08]"
                      >
                        <FileText className="h-3.5 w-3.5 text-primary" />
                        {t('admin.form.view_application')}
                        <ExternalLink className="h-3 w-3 text-ink/40" />
                      </Link>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
        </Card>
      )}

      {selectedFlat && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <h3 className="m-0 font-display text-base font-bold tracking-tight text-ink">
              {t('admin.form.selected_flat')}
            </h3>
          </div>
          <Card variant="light" className="flex items-center gap-4 p-4">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-ink/[0.04]">
              {selectedFlat.main_image && (
                <img
                  src={`${SNA_IMAGE_PREFIX}${selectedFlat.main_image}`}
                  alt={selectedFlat.title ?? ''}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden'
                  }}
                />
              )}
            </div>
            <div className="min-w-0">
              <div className="truncate font-display text-base font-bold text-ink">
                {selectedFlat.title}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                {selectedFlat.location?.village ||
                  selectedFlat.location?.district ||
                  selectedFlat.location?.region ||
                  '—'}
                {selectedFlat.area ? ` · ${selectedFlat.area} m²` : ''}
              </div>
            </div>
          </Card>
        </Card>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={depositOverCap}>
          <Save className="h-4 w-4" />
          {t('admin.form.submit_offers')}
        </Button>
      </div>
    </form>
  )
}
